import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import { useCallback, useEffect, useMemo, useRef } from "react";
import Helpers from "@/Helpers";
import RankInputControl from "@/elements/RankInputControl";
import { resolveSectionRatingInput } from "@/sections/rankInput";

export default function Colorsheet(props) {
   const { sectionData, values, setValue } = props;
   const firstInputRef = useRef(null);
   const currentSection = sectionData?.currentSection;
   const content: Record<string, string> = Helpers.getSectionContent(currentSection, sectionData?.lang);
   const elements = Helpers.getSectionElements(content);
   const ratingInput = useMemo(() => resolveSectionRatingInput(elements), [elements]);
   const contentSquares = Array.isArray(elements?.items) ? elements.items : [];
   const contentOops = elements?.oops || {};
   const instructionsHtml = Helpers.getSectionHtmlValue(content, "instructions");

   function getSquareHtml(square, index) {
      return Helpers.getColorsheetSquareHtml(content, square, index);
   }

   const getIssueCount = useCallback(function (validationValues) {
      let totalIssueCount = 0;
      const valueMap = new Map();
      const duplicateValues = new Set();
      for (let i = 1; i <= 4; i++) {
         const value = validationValues[currentSection.formId + i];
         if (value === undefined || value <= 0) {
            totalIssueCount++;
         }
         else {
            if (valueMap.has(value)) {
               duplicateValues.add(value);
            } else {
               valueMap.set(value, i);
            }
         }
      }
      for (let i = 1; i <= 4; i++) {
         const value = validationValues[currentSection.formId + i];
         if (value !== undefined && value > 0 && duplicateValues.has(value)) {
            totalIssueCount++;
         }
      }
      return totalIssueCount;
   }, [currentSection?.formId]);

   useEffect(() => {
      sectionData.registerSection( 400);
      const focusFirstInput = () => {
         if (!firstInputRef.current) return;
         firstInputRef.current.focus();
         firstInputRef.current.select?.();
      };

      const timeoutId = setTimeout(() => {
         requestAnimationFrame(() => {
            requestAnimationFrame(focusFirstInput);
         });
      }, 0);

      return () => clearTimeout(timeoutId);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentSection?.id]);

   const tempValues = {};
   for (let i = 401; i <= 404; i++)
      tempValues[i] = values?.[i] ?? '';
   const issueCount = getIssueCount(tempValues)
   const handleKeyDown = (e) => {
      if (props?.sectionData?.onKeyDown) {
         props.sectionData.onKeyDown(e);
      }
      if (props?.sectionData?.isAdmin) {
         if (e.key === 'Enter' && issueCount === 0) {
            sectionData.onNextSection();
         }
         else if (['1', '2', '3', '4'].includes(e.key)) {
            const currentTarget = e.currentTarget;
            const currentIndex = parseInt(currentTarget.tabIndex);

            setTimeout(() => {
               let nextEmptyIndex = -1;
               for (let i = currentIndex + 1; i <= 4; i++) {
                  const nextInput = document.querySelector(`input[tabindex="${i}"], select[tabindex="${i}"]`) as HTMLElement | null;
                  const fieldValue = values[currentSection.formId + i];
                  const hasValue = fieldValue !== undefined && fieldValue !== "" && Number(fieldValue) > 0;
                  if (nextInput && !hasValue) {
                     nextEmptyIndex = i;
                     break;
                  }
               }

               if (nextEmptyIndex > 0) {
                  const nextInput = document.querySelector(`input[tabindex="${nextEmptyIndex}"], select[tabindex="${nextEmptyIndex}"]`) as HTMLInputElement | null;
                  if (nextInput) {
                     nextInput.focus();
                     nextInput.select?.();
                  }
               }
            }, 10);
         }
      }
   };

   useEffect(() => {
      const nextCount = Number(sectionData?.nextCount || 0);
      const issueCountForAdvance = nextCount > 0 ? getIssueCount(values || {}) : 0;
      sectionData?.updateIssueCount?.(issueCountForAdvance);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentSection?.formId, getIssueCount, sectionData?.nextCount, sectionData?.updateIssueCount, values]);

   function options(index, column) {
      const value = values[currentSection.formId + index] ?? "";
      const valuesArray = Object.values(values);
      const numberOfProperties = valuesArray.length;
      const isDuplicate = numberOfProperties > 0 && valuesArray.filter(v => v === value).length > 1;
      const isMissing = value === undefined || !value || value === -1;
      const square = contentSquares[index - 1] || {};
      const colorClass = Helpers.resolveColorsheetColorKey(square, index);
      const fieldInvalid = sectionData.showValidations && (isMissing || (value > 0 && isDuplicate));

      return (
         <>
            <div className={`column ${colorClass}`} data-column={column}>
               <div className="text">{parseSanitizedHtml(getSquareHtml(square, index))}</div>
               <RankInputControl
                  ref={index === 1 ? firstInputRef : null}
                  ratingInput={ratingInput}
                  value={value}
                  invalid={fieldInvalid}
                  showValidations={sectionData.showValidations}
                  inputClassName="colorInput"
                  selectClassName="colorInput checklistRatingSelect"
                  id={`colorsheet-${currentSection?.id || currentSection?.formId || "section"}-${index}`}
                  name={`colorsheet-${currentSection?.id || currentSection?.formId || "section"}-${index}`}
                  ariaLabel={`Color ranking for item ${index}`}
                  tabIndex={index}
                  dataId={index}
                  isAdmin={!!sectionData?.isAdmin}
                  onKeyDown={handleKeyDown}
                  onCommit={(next) => setValue(index, next === "" ? "" : next)}
               />
               {sectionData.showValidations && isMissing && (
                  <span className="validation">{contentOops["missing"]}</span>
               )}
               {sectionData.showValidations && value > 0 && isDuplicate && (
                  <span className="validation">{contentOops["repeated"]}</span>
               )}
            </div>
         </>
      );
   }
   return (
      instructionsHtml && (
         <div className={`page ${sectionData.lang && sectionData.lang !== "eng" ? "ColorSheetNonEnglish ColorSheetEsp" : "ColorSheet"}`}>

         <div className="instructions">{parseSanitizedHtml(instructionsHtml)}</div>

            <div className="content">
               {options(1, 1)}
               {options(2, 2)}
               {options(3, 1)}
               {options(4, 2)}
            </div>
         </div>
      ));
}
